import { Autocomplete, Alert, Box, Button, Card, CardContent, Container, FormControl, FormHelperText, Grid, InputLabel, MenuItem, Select, TextField, Typography, CircularProgress } from '@mui/material';
import React, { useEffect, useState } from 'react'
import PageLayout from '../../components/layout/PageLayout';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useSearchParams } from 'react-router-dom';
import IconButton from "@mui/material/IconButton";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { toast } from 'react-toastify';
import { ORDER_LIST_PATH, LEADS_PATH } from '../../navigations/path';
import {
    useCreateOrderMutation,
    useLazyGetLeadsQuery,
    useLazyGetLeadByIdQuery,
    useLazyGetServicesQuery,
} from '../../store/api';
import { useLazySelectOptions } from '../../hooks/useLazySelectOptions';
import { useServerValidationErrors } from '../../hooks/useServerValidationErrors';
import { extractList } from '../../utils/response';

/** Matches BillingCycleEnum::ONETIME on the API — a hyphen, not an underscore. */
const ONE_TIME = 'one-time';

/** Must match SaleTypeEnum on the API; anything else fails validation with 422. */
const SALE_TYPES = [
  { value: 'front-sale', label: 'Front Sale' },
  { value: 'up-sale', label: 'Up Sale' },
  { value: 'front-sale-referral', label: 'Front Sale (Referral)' },
  { value: 'up-sale-referral', label: 'Up Sale (Referral)' },
  { value: 'down-sell', label: 'Down Sell' },
];

const CreateOrder = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const leadIdFromQuery = searchParams.get("leadId") || "";
    const isLeadLocked = Boolean(leadIdFromQuery);

    // const [amount, setAmount] = useState("");
    const [saleType, setSaleType] = useState(SALE_TYPES[0].value);
    const [city, setCity] = useState("");
    const [state, setState] = useState("");
    const [zip, setZip] = useState("");
    const [address, setAddress] = useState("");
    const [description, setDescription] = useState("");
    const [selectedLeadID, setSelectedLeadID] = useState(null);
    const [lockedLeadResolving, setLockedLeadResolving] = useState(
        () => isLeadLocked,
    );

    const [services, setServices] = useState([
        {
            service: null,
            price: "",
            billing_cycle: ONE_TIME,
        },
    ]);

    const [invoices, setInvoices] = useState([
        {
            amount: "",
            description: "",
            expires_at: "",
        },
    ]);

    const [fetchLeads] = useLazyGetLeadsQuery();
    const [fetchLeadById] = useLazyGetLeadByIdQuery();
    const [fetchServices] = useLazyGetServicesQuery();

    const [createOrder, { isLoading: submitting }] = useCreateOrderMutation();
    const { errors, applyApiError, clearError, fieldProps } =
        useServerValidationErrors();
    const rowErrors = Object.entries(errors).filter(
        ([key, msg]) => msg && (key.startsWith("services") || key.startsWith("invoices"))
    );

    const leadSelect = useLazySelectOptions(fetchLeads, {
        args: { paginate: 0 },
        mapResult: (res) => {
            const list = res?.data ?? extractList(res);
            return (Array.isArray(list) ? list : []).map((item) => ({
                id: item.id,
                label: item.name
                    ? `${item.name}${item.email ? ` (${item.email})` : ""}`
                    : item.email || String(item.id),
            }));
        },
        enabled: !isLeadLocked,
    });

    const serviceSelect = useLazySelectOptions(fetchServices, {
        args: { paginate: 0 },
        mapResult: (res) => {
            const list = res?.data ?? extractList(res);
            return (Array.isArray(list) ? list : []).map((item) => ({
                id: item.id,
                label: item.name,
                internal_charges: Number(item.internal_charges),
                external_charges: Number(item.external_charges),
                is_recurring: item.is_recurring,
            }));
        },
    });

    useEffect(() => {
        if (!isLeadLocked) return undefined;
        let cancelled = false;
        setLockedLeadResolving(true);
        (async () => {
            try {
                const data = await fetchLeadById(leadIdFromQuery).unwrap();
                const lead = data?.data ?? data;
                if (cancelled) return;
                if (!lead?.id) {
                    toast.error("Lead not found");
                    setSelectedLeadID(null);
                    return;
                }
                setSelectedLeadID({
                    id: lead.id,
                    label: lead.name
                        ? `${lead.name}${lead.email ? ` (${lead.email})` : ""}`
                        : lead.email || String(lead.id),
                });
            } catch (err) {
                if (!cancelled) {
                    toast.error(err?.data?.message || "Failed to load lead");
                    setSelectedLeadID(null);
                }
            } finally {
                if (!cancelled) setLockedLeadResolving(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isLeadLocked, leadIdFromQuery, fetchLeadById]);

    const loadServices = () =>
        serviceSelect.load().catch(() => {
            toast.error('Failed to fetch services');
        });

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const payload = {
                lead_id: selectedLeadID?.id,

                services: services.map((item) => ({
                    id: item.service?.id,
                    price: Number(item.price),
                    billing_cycle: item.billing_cycle,
                })),

                // amount: Number(amount),
                sale_type: saleType,
                city,
                state,
                zip,
                address,
                description,

                invoices: invoices.map((item) => ({
                    amount: Number(item.amount),
                    description: item.description,
                    expires_at: item.expires_at,
                })),
            };


            const data = await createOrder(payload).unwrap();


            toast.success(data?.message || "Order created successfully.");

            navigate(ORDER_LIST_PATH);
        } catch (error) {
            applyApiError(error, "Failed to create order.");
        }
    };

    const addService = () => {
        setServices([
            ...services,
            {
                service: null,
                price: "",
                billing_cycle: ONE_TIME,
            },
        ]);
    };

    const removeService = (index) => {
        setServices(services.filter((_, i) => i !== index));
    };

    const updateService = (index, field, value) => {
        const temp = [...services];
        temp[index][field] = value;
        setServices(temp);
    };

    const addInvoice = () => {
        setInvoices([
            ...invoices,
            {
                amount: "",
                description: "",
                expires_at: "",
            },
        ]);
    };

    const removeInvoice = (index) => {
        setInvoices(invoices.filter((_, i) => i !== index));
    };

    const updateInvoice = (index, field, value) => {
        const temp = [...invoices];
        temp[index][field] = value;
        setInvoices(temp);
    };

    return (
        <PageLayout>
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 2,
                    }}
                >
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() =>
                            navigate(isLeadLocked ? LEADS_PATH : ORDER_LIST_PATH)
                        }
                    >
                        Back
                    </Button>
                </Box>

                <Typography
                    variant="h4"
                    sx={{
                        mb: 3,
                        fontWeight: 600,
                        color: "primary.main",
                    }}
                >
                    Create Order
                </Typography>

                <Card>
                    <CardContent component="form"
                        onSubmit={handleSubmit}
                        sx={{ p: 3 }}>
                        <Grid container spacing={2}>
                            {isLeadLocked ? (
                                <Grid item xs={12}>
                                    {lockedLeadResolving ? (
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 1 }}>
                                            <CircularProgress size={24} />
                                            <Typography color="text.secondary">Loading lead…</Typography>
                                        </Box>
                                    ) : selectedLeadID ? (
                                        <Alert severity="info" sx={{ py: 1.5 }}>
                                            <strong>Lead:</strong> {selectedLeadID.label}
                                        </Alert>
                                    ) : (
                                        <Alert severity="error">
                                            This lead could not be loaded. Return to the{" "}
                                            <Typography
                                                component="span"
                                                sx={{ textDecoration: "underline", cursor: "pointer" }}
                                                onClick={() => navigate(LEADS_PATH)}
                                            >
                                                leads list
                                            </Typography>
                                            .
                                        </Alert>
                                    )}
                                </Grid>
                            ) : (
                                <Grid item xs={12}>
                                    <Autocomplete
                                        options={leadSelect.options}
                                        value={selectedLeadID}
                                        onChange={(_, value) => {
                                            setSelectedLeadID(value);
                                            clearError("lead_id");
                                        }}
                                        onOpen={() => leadSelect.load()}
                                        loading={leadSelect.loading}
                                        getOptionLabel={(option) => option?.label || ""}
                                        isOptionEqualToValue={(option, value) =>
                                            option?.id === value?.id
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Lead"
                                                required
                                                {...fieldProps("lead_id")}
                                            />
                                        )}
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <FormControl fullWidth error={!!errors.sale_type}>
                                    <InputLabel>Sale Type</InputLabel>
                                    <Select
                                        value={saleType}
                                        label="Sale Type"
                                        onChange={(e) => {
                                            setSaleType(e.target.value);
                                            clearError("sale_type");
                                        }}
                                    >
                                        {SALE_TYPES.map((type) => (
                                            <MenuItem key={type.value} value={type.value}>
                                                {type.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors.sale_type && (
                                        <FormHelperText>{errors.sale_type}</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>

                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="City"
                                    value={city}
                                    onChange={(e) => {
                                        setCity(e.target.value);
                                        clearError("city");
                                    }}
                                    {...fieldProps("city")}
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="State"
                                    value={state}
                                    onChange={(e) => {
                                        setState(e.target.value);
                                        clearError("state");
                                    }}
                                    {...fieldProps("state")}
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Zip"
                                    value={zip}
                                    onChange={(e) => {
                                        setZip(e.target.value);
                                        clearError("zip");
                                    }}
                                    {...fieldProps("zip")}
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    label="Address"
                                    value={address}
                                    onChange={(e) => {
                                        setAddress(e.target.value);
                                        clearError("address");
                                    }}
                                    {...fieldProps("address")}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Description"
                                    multiline
                                    rows={3}
                                    value={description}
                                    onChange={(e) => {
                                        setDescription(e.target.value);
                                        clearError("description");
                                    }}
                                    {...fieldProps("description")}
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <Typography variant="h6">
                                    Services
                                </Typography>
                            </Grid>

                            <Grid item xs={6} sx={{ display: "flex", justifyContent: "flex-end" }}>
                                < Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={addService}
                                    startIcon={<AddCircleOutlineIcon />}
                                >
                                    Add
                                </Button>
                            </Grid>

                            {services.map((item, index) => (
                                <React.Fragment key={index}>
                                    <Grid item xs={12} md={4}>
                                        {/* <Autocomplete
                                            options={serviceOptions}
                                            value={item.service}
                                            onChange={(_, value) =>
                                                updateService(index, "service", value)
                                            }
                                            getOptionLabel={(option) => option?.label || ""}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Service" />
                                            )}
                                        /> */}

                                        <Autocomplete
                                            options={serviceSelect.options}
                                            value={item.service}
                                            // onChange={(_, value) =>
                                            //     updateService(index, "service", value)
                                            // }
                                            onChange={(_, value) => {
                                                updateService(index, "service", value);

                                                if (value) {
                                                    updateService(index, "price", value.external_charges);

                                                    // Non-recurring services bill once — the API
                                                    // rejects anything outside BillingCycleEnum,
                                                    // whose one-off value is "one-time".
                                                    updateService(
                                                        index,
                                                        "billing_cycle",
                                                        value.is_recurring ? "monthly" : ONE_TIME
                                                    );
                                                }
                                            }}
                                            onOpen={loadServices}
                                            loading={serviceSelect.loading}
                                            getOptionLabel={(option) => option?.label || ""}
                                            isOptionEqualToValue={(option, value) =>
                                                option.id === value.id
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Service"
                                                    required
                                                />
                                            )}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            label="Price"
                                            type="number"
                                            fullWidth
                                            value={item.price}
                                            onChange={(e) =>
                                                updateService(index, "price", e.target.value)
                                            }
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={3}>
                                        <FormControl fullWidth disabled={!item.service?.is_recurring}>
                                            <InputLabel>Billing</InputLabel>
                                            <Select
                                                value={item.billing_cycle}
                                                label="Billing"
                                                onChange={(e) =>
                                                    updateService(
                                                        index,
                                                        "billing_cycle",
                                                        e.target.value
                                                    )
                                                }
                                            >
                                                {/* A non-recurring service can only bill once, so
                                                    the cycle is fixed and the field is disabled. */}
                                                {item.service?.is_recurring ? (
                                                    [
                                                        <MenuItem key="monthly" value="monthly">Monthly</MenuItem>,
                                                        <MenuItem key="yearly" value="yearly">Yearly</MenuItem>,
                                                    ]
                                                ) : (
                                                    <MenuItem value={ONE_TIME}>One-time</MenuItem>
                                                )}
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    <Grid item xs={12} md={2}>
                                        <IconButton
                                            color="error"
                                            onClick={() => removeService(index)}
                                        >
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </Grid>
                                </React.Fragment>
                            ))}

                            <Grid item xs={6}>
                                <Typography variant="h6">
                                    Invoices
                                </Typography>
                            </Grid>
                            <Grid item xs={6} sx={{ display: "flex", justifyContent: "flex-end" }}>
                                < Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={addInvoice}
                                    startIcon={<AddCircleOutlineIcon />}
                                >
                                    Add
                                </Button>
                            </Grid>

                            {invoices.map((item, index) => (
                                <React.Fragment key={index}>
                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            label="Amount"
                                            type="number"
                                            fullWidth
                                            value={item.amount}
                                            onChange={(e) =>
                                                updateInvoice(index, "amount", e.target.value)
                                            }
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={4}>
                                        <TextField
                                            label="Description"
                                            fullWidth
                                            value={item.description}
                                            onChange={(e) =>
                                                updateInvoice(index, "description", e.target.value)
                                            }
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={3}>
                                        <TextField
                                            type="date"
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                            label="Expires At"
                                            value={item.expires_at}
                                            onChange={(e) =>
                                                updateInvoice(index, "expires_at", e.target.value)
                                            }
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={2}>
                                        <IconButton
                                            color="error"
                                            onClick={() => removeInvoice(index)}
                                        >
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </Grid>
                                </React.Fragment>
                            ))}
                            {/* Row-level errors (services / invoices) have no single field to sit on */}
                            {rowErrors.length > 0 && (
                                <Grid item xs={12}>
                                    <Alert severity="error">
                                        {rowErrors.map(([key, msg]) => (
                                            <div key={key}>{msg}</div>
                                        ))}
                                    </Alert>
                                </Grid>
                            )}

                            {/* Buttons */}
                            <Grid
                                item
                                xs={12}
                                sx={{
                                    display: "flex",
                                    gap: 2,
                                    mt: 1,
                                }}
                            >
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={
                                        submitting ||
                                        (isLeadLocked &&
                                            (lockedLeadResolving || !selectedLeadID))
                                    }
                                >
                                    {submitting ? (
                                        <CircularProgress size={22} />
                                    ) : false ? (
                                        "Edit Order"
                                    ) : (
                                        "Create Order"
                                    )}
                                </Button>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Container>
        </PageLayout>
    )
}

export default CreateOrder

